/**
 * API Client – fetch wrapper with JWT auto-refresh, error normalization.
 * Mirrors the token management pattern from the main IdeaPilot frontend.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const TOKEN_KEY = 'ipv_access';
const REFRESH_KEY = 'ipv_refresh';
const USER_KEY = 'ipv_user';
const TIMEOUT_MS = 5 * 60 * 1000; // 5 min for AI calls

// ── Token storage ────────────────────────────────────────────────

export function saveTokens(data: {
  accessToken: string;
  refreshToken: string;
  user: Record<string, unknown>;
}) {
  sessionStorage.setItem(TOKEN_KEY, data.accessToken);
  sessionStorage.setItem(REFRESH_KEY, data.refreshToken);
  sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}

export function getStoredUser(): Record<string, unknown> | null {
  const raw = sessionStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function clearTokens() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(USER_KEY);
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!));
    // 30-second skew
    return (payload.exp as number) * 1000 < Date.now() + 30_000;
  } catch {
    return true;
  }
}

// ── API error ────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// ── Token refresh ────────────────────────────────────────────────

let refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const rt = getRefreshToken();
    if (!rt) throw new ApiError('No refresh token', 401);

    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });

    if (!res.ok) {
      clearTokens();
      throw new ApiError('Session expired', 401);
    }

    const data = (await res.json()) as {
      accessToken: string;
      refreshToken: string;
      user: Record<string, unknown>;
    };
    saveTokens(data);
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

// ── Core fetch ───────────────────────────────────────────────────

async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const { skipAuth, ...fetchOpts } = options;
  const url = `${API_BASE}${path}`;

  const headers = new Headers(fetchOpts.headers);
  if (!headers.has('Content-Type') && fetchOpts.body && typeof fetchOpts.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  // Attach bearer token
  if (!skipAuth) {
    let token = getAccessToken();
    if (token && isTokenExpired(token)) {
      await refreshAccessToken();
      token = getAccessToken();
    }
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(url, {
      ...fetchOpts,
      headers,
      signal: controller.signal,
    });

    if (res.status === 401 && !skipAuth) {
      // Try refresh once
      try {
        await refreshAccessToken();
      } catch {
        throw new ApiError('Session expired', 401);
      }
      const retryToken = getAccessToken();
      if (retryToken) headers.set('Authorization', `Bearer ${retryToken}`);
      const retryRes = await fetch(url, { ...fetchOpts, headers });
      if (!retryRes.ok) {
        const errData = await retryRes.json().catch(() => ({}));
        throw new ApiError(
          (errData as { error?: string }).error || `HTTP ${retryRes.status}`,
          retryRes.status,
          errData
        );
      }
      return (await retryRes.json()) as T;
    }

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new ApiError(
        (errData as { error?: string }).error || `HTTP ${res.status}`,
        res.status,
        errData
      );
    }

    // 204 No Content
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } finally {
    clearTimeout(timeout);
  }
}

// ── Convenience methods ──────────────────────────────────────────

export const api = {
  get: <T = unknown>(path: string, opts?: RequestInit) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, {
      ...opts,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T = unknown>(path: string, body?: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, {
      ...opts,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, {
      ...opts,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T = unknown>(path: string, opts?: RequestInit) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
